import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n);
  } catch {
    return `${n.toFixed(2)}`;
  }
}

export default function InvoicePayView({
  fetchInvoice,
  onCheckout,
  pdfHref,
  compact = false,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const reload = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const json = await fetchInvoice();
      setPayload(json);
    } catch (e) {
      setError(e.message || 'Failed to load invoice');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [fetchInvoice]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function handlePayByCard() {
    if (!onCheckout) return;
    setError('');
    setBusy(true);
    try {
      const base = window.location.href.split('?')[0];
      const successUrl = `${base}?paid=1`;
      const cancelUrl = base;
      const { checkout_url: url } = await onCheckout(successUrl, cancelUrl);
      if (url) window.location.href = url;
    } catch (e) {
      setError(e.message || 'Could not start card payment');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={compact ? 28 : 36} />
      </Box>
    );
  }

  const invoice = payload?.invoice;
  const payment = payload?.payment || {};
  const paid = invoice?.paid;
  const stripeOn = !!payment?.stripe_enabled;

  if (!invoice) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  const bank = payment?.bank || {};

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {paid ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          This invoice has been paid. Thank you.
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: compact ? 2 : 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>Invoice {invoice.invoice_number}</Typography>
              <Typography variant="body2" color="text.secondary">
                {invoice.client_name}
                {invoice.due_date ? ` · Due ${invoice.due_date}` : ''}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.25rem' }}>
              {money(invoice.total)}
            </Typography>
          </Box>

          {invoice.description ? (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {invoice.description}
            </Typography>
          ) : null}

          <Divider sx={{ my: 2 }} />

          {!paid && (bank.name || bank.account) ? (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>Pay by bank transfer</Typography>
              {bank.name ? <Typography variant="body2">Bank: {bank.name}</Typography> : null}
              {bank.sort_code ? <Typography variant="body2">Sort code: {bank.sort_code}</Typography> : null}
              {bank.account ? <Typography variant="body2">Account: {bank.account}</Typography> : null}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Reference: <b>{invoice.invoice_number}</b>
                {bank.reference_hint ? ` — ${bank.reference_hint}` : ''}
              </Typography>
            </Box>
          ) : null}

          {payment.instructions ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {payment.instructions}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!paid && stripeOn && onCheckout ? (
              <Button
                variant="contained"
                startIcon={<CreditCardIcon />}
                disabled={busy}
                onClick={handlePayByCard}
                sx={{ textTransform: 'none' }}
              >
                Pay by card
              </Button>
            ) : null}
            {pdfHref ? (
              <Button
                component="a"
                href={pdfHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                sx={{ textTransform: 'none' }}
              >
                Download PDF
              </Button>
            ) : null}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
